import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { CSSProperties, useState } from 'react';

import { MotionContainer, varFade } from '@components/Animate';
import { m } from 'framer-motion';

import { CaptureId, CurrentSessionProps, DrawerTypes, IdV4, MapCapturePiece, RecipientStatus } from '../../types';
import MailPieceList from './components/MailPieceList';
import PieceView from './components/PieceView';
import ProgressList from './components/ProgressList';
import {
  animationOpenClosed,
  sxCenter,
  sxGridChild_1,
  sxGridChild_1_1,
  sxGridChild_1_2,
  sxGridChild_2,
  sxGridContainer,
  sxImgEmpty_160,
} from './styles';
import { GroupPiecesStatus, ProgressStatus } from './types';
import { getPiecesByStatus } from './utils/getPieces';

type ReviewItemsProps = {
  mapCapturePiece: MapCapturePiece;
  setDrawer: (type: DrawerTypes) => void;
  setCursor: (key: CaptureId | null) => void;
  onCropClick: () => void;
  retryFailedPieces: (id: IdV4) => void;
  requestedReceiveSession: CurrentSessionProps;
  onDeletePiece: () => void;
  cursor: CaptureId | null;
};

const ReviewItems = ({
  setDrawer,
  setCursor,
  onCropClick,
  retryFailedPieces,
  onDeletePiece,
  cursor,
  mapCapturePiece,
  requestedReceiveSession,
}: ReviewItemsProps) => {
  const [status, setStatus] = useState<ProgressStatus>(GroupPiecesStatus.all);
  const [searchText, setSearchText] = useState('');
  const capturePieces = Object.values(mapCapturePiece);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const pieceByStatus = getPiecesByStatus(capturePieces, searchText);
  const lengthByStatus = {
    [GroupPiecesStatus.all]: pieceByStatus[GroupPiecesStatus.all].length,
    [RecipientStatus.ready]: pieceByStatus[RecipientStatus.ready].length,
    [RecipientStatus.review]: pieceByStatus[RecipientStatus.review].length,
    [RecipientStatus.invalid]: pieceByStatus[RecipientStatus.invalid].length,
    [GroupPiecesStatus.failed]: pieceByStatus.failed.length,
  };

  const selectPiceId = (pieceId: CaptureId | null) => {
    setCursor(pieceId);
  };

  const activePiece = cursor ? mapCapturePiece[cursor] : null;

  const isInList = pieceByStatus[status].some(({ id }) => id === cursor);

  if (!isInList && cursor) {
    selectPiceId(pieceByStatus[status][0]?.id || null);
  }

  if (cursor && mapCapturePiece[cursor] && !activePiece) {
    selectPiceId(cursor);
  }

  const changeProgressStatus = (data: ProgressStatus) => {
    setStatus(data);
    if (!matches) {
      const isInNextList = pieceByStatus[data].some(({ id }) => id === cursor);
      if (isInNextList || !capturePieces.length) return;
      if (data === GroupPiecesStatus.all) {
        selectPiceId(capturePieces[0].id || null);
      } else {
        selectPiceId(pieceByStatus[data][0]?.id || null);
      }
    }
  };

  return (
    <Grid container component={MotionContainer} columnSpacing={{ xs: 0, md: 1 }} sx={sxGridContainer}>
      <Grid container size={{ xs: 12, md: 5 }} sx={sxGridChild_1}>
        <Grid size={{ md: 4 }} sx={sxGridChild_1_1}>
          <ProgressList onChange={changeProgressStatus} status={status} data={lengthByStatus} />
        </Grid>
        <Grid container size={{ md: 8 }} sx={sxGridChild_1_2}>
          <MailPieceList
            onSearch={setSearchText}
            handleClick={setCursor}
            pieces={pieceByStatus[status]}
            activePiece={activePiece}
            status={status}
          />
        </Grid>
      </Grid>
      <Grid
        component={m.div}
        container
        initial="closed"
        animate={activePiece ? 'open' : 'closed'}
        variants={matches ? animationOpenClosed : {}}
        transition={varFade('inRight')}
        size={{ md: 7 }}
        sx={sxGridChild_2}
      >
        {activePiece ? (
          <PieceView
            setDrawer={setDrawer}
            capturePiece={activePiece}
            onCropClick={onCropClick}
            retryFailedPieces={retryFailedPieces}
            currentSession={requestedReceiveSession}
            onDeletePiece={onDeletePiece}
            handleBack={() => {
              setCursor(null);
            }}
          />
        ) : (
          <Box bgcolor="background.paper" width={1} position="relative" borderRadius={1.5}>
            <img
              src="/assets/icons/empty/ic_email_disabled.svg"
              style={{ ...(sxCenter as CSSProperties), ...sxImgEmpty_160 }}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default ReviewItems;
